import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { ApiError, api, can, refusalReason, shortDate, type RiskModel } from '../lib/api'
import { DataTable, ErrorBox, Loading, type Column } from '../components/DataTable'
import '../styles/studio.css'

export function RiskModels() {
  const [list, setList] = useState<RiskModel[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)

  const load = useCallback(() => {
    api
      .riskModels()
      .then((r) => {
        setList(r.riskModels)
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])

  function create(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaveError(null)
    api
      .createRiskModel({ code: code.trim(), name: name.trim(), description })
      .then((r) => {
        setOpen(false)
        setCode('')
        setName('')
        setDescription('')
        window.location.hash = '#/risk-model/' + r.id
      })
      .catch((err) => setSaveError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setSaving(false))
  }

  const columns: Column<RiskModel>[] = [
    {
      key: 'code',
      header: 'Model',
      sortValue: (m) => m.code,
      render: (m) => (
        <a href={'#/risk-model/' + encodeURIComponent(m.id)}>
          <span className="cell-primary mono">{m.code}</span>
          <span className="cell-sub">{m.name}</span>
        </a>
      ),
    },
    {
      key: 'default',
      header: 'Default',
      sortValue: (m) => (m.isDefault ? 0 : 1),
      render: (m) =>
        m.isDefault ? <span className="pill pill-on">Tenant default</span> : <span className="muted">no</span>,
    },
    {
      key: 'active',
      header: 'Active version',
      sortValue: (m) => m.activeVersionNo ?? -1,
      render: (m) =>
        m.activeVersionId ? (
          <span className="pill pill-on">v{m.activeVersionNo}</span>
        ) : (
          <span className="pill pill-off">nothing active</span>
        ),
    },
    {
      key: 'drafts',
      header: 'Drafts',
      numeric: true,
      sortValue: (m) => m.draftCount,
      render: (m) =>
        m.draftCount > 0 ? <span className="pill pill-pending">{m.draftCount}</span> : <span className="muted">0</span>,
    },
    {
      key: 'versions',
      header: 'Versions',
      numeric: true,
      sortValue: (m) => m.versionCount,
      render: (m) => m.versionCount,
    },
    {
      key: 'created',
      header: 'Created',
      sortValue: (m) => m.createdAt,
      render: (m) => shortDate(m.createdAt),
    },
  ]

  if (error) return <ErrorBox error={error} what="Risk models" />
  if (!list) return <Loading what="risk models" />

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Risk models</h1>
        </div>
        <button
          type="button"
          className="primary"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          disabled={!can('riskmodel.author')}
          title={can('riskmodel.author') ? undefined : refusalReason('riskmodel.author')}
        >
          New risk model
        </button>
      </div>

      {open && (
        <form className="panel" onSubmit={create}>
          <h2>New risk model</h2>
          {saveError && <ErrorBox error={saveError} what="The risk model" />}
          <div className="form-grid">
            <div className="field">
              <label htmlFor="m-code">Code</label>
              <input
                id="m-code"
                className="mono"
                required
                value={code}
                spellCheck={false}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            </div>
            <div className="field">
              <label htmlFor="m-name">Name</label>
              <input id="m-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="m-desc">Description</label>
            <textarea id="m-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="commit-bar">
            <button
              type="submit"
              className="primary"
              disabled={saving || !code.trim() || !name.trim()}
              data-action="tprm.risk-models.a01"
            >
              {saving ? 'Creating' : 'Create and open'}
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <span className="muted">The model has no versions yet. Draft one, then promote it.</span>
          </div>
        </form>
      )}

      <DataTable
        caption="Risk models in this tenant"
        columns={columns}
        rows={list}
        rowKey={(m) => m.id}
        defaultSort={{ key: 'code', dir: 'asc' }}
        empty={{
          title: 'No risk models.',
          detail: 'Nothing can be scored until one is promoted.',
        }}
      />
    </>
  )
}
